/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Input, Space, Drawer, Layout, message, Modal, Radio } from 'antd';
import { MessageOutlined, LoadingOutlined } from '@ant-design/icons';
import { Button } from '@crc-evans/ui-components';
import { FileText, Download, Mail, PenLine, Save } from 'lucide-react';
import DSRSignaturePad from '../DSR/DSRSignaturePad';
import axiosConfig from '../../services/axiosConfig';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { triggerReportDownload, getDprReportFilename } from '../../utils/reportDownloadUrl';
import {
  generateDprReport,
  generateFlashReport,
  refreshReportDownloadUrl,
  getStoredDprReportEntry,
  isReportReadyForDownload,
  hasGeneratedStoredReport,
  buildReportEntryFromGenerateResponse,
  upsertDprReportDetailsEntry,
  mapUiReportTypeToApi,
  mapUiOutputToApi,
  formatDprReportGeneratedLabel,
  extractReportGeneratedAt,
  DPR_REPORT_STATUS,
} from '../../services/dprReport';
import { isOffshoreProject } from '../../utils/projectConfig';
import DprSubmitReviewModal from './DprSubmitReviewModal';
import {
  fetchDprProductivityLogCached,
  buildMainlineProductivitySummaries,
  getMainlineDisplayTitle,
} from './ProductivityMainlinePanel';
import '../DSR/DownTimeModal.css';
import '../GenerateReport.css';

const { Title } = Typography;
const { TextArea } = Input;
const { Content, Footer } = Layout;

const DPR_REPORT_DESTINATION_KEY = '10';
const DPR_DOWNLOAD_PDF_LABEL = 'Download PDF Report';

const getConfiguredDprReportEmails = (projectPayload) => {
  const user = Array.isArray(projectPayload) ? projectPayload[0] : projectPayload;
  const reportDestinationEmails = user?.config?.reportDestinationEmails;
  if (!reportDestinationEmails) return [];
  const list = reportDestinationEmails[DPR_REPORT_DESTINATION_KEY] || [];
  return [...new Set(list.map((e) => String(e).trim()).filter(Boolean))];
};

const commentsFieldGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '16px',
  marginBottom: 'var(--modern-spacing-lg, 24px)',
};

const commentsFieldCellStyle = { minWidth: 0, width: '100%' };

const commentsSignatureBoxStyle = {
  width: '100%',
  boxSizing: 'border-box',
};

const CommentBox = ({ header, value, onChange, placeholder }) => (
  <div style={{ marginBottom: 'var(--modern-spacing-lg, 24px)' }}>
    <h4 style={{
      margin: '0 0 12px 0',
      fontSize: 'var(--modern-font-size-base, 14px)',
      fontWeight: 500,
      color: 'var(--modern-text-primary, #374151)',
      textAlign: 'left'
    }}>
      {header}
    </h4>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={8}
      style={{
        width: '100%',
        padding: '12px',
        fontSize: 'var(--modern-font-size-base, 14px)',
        border: '1px solid var(--modern-border-color, #E5E7EB)',
        borderRadius: 'var(--modern-border-radius, 8px)',
        resize: 'vertical',
        outline: 'none',
        fontFamily: 'inherit',
        color: 'var(--modern-text-primary, #1F2937)',
        backgroundColor: '#FFFFFF',
        lineHeight: '20px'
      }}
    />
  </div>
);

const CommentsLog = ({
  commentsData: initialCommentsData = {},
  onSave,
  onCancel,
  onGenerateReport,
  isSubmitting = false,
  shift = 'day',
  dprLogId,
  dprStatus = null,
  reportDetails: dprReportDetails = null,
  onReportGenerated,
  skipDayShift = false,
  skipNightShift = false,
  skipDpr = false,
  selectedProject,
  logDate,
  sowId,
  ongoingPipelineId,
  dprProjectStatus = '',
  requireOngoingPipeline = false,
  projectMainlines = [],
  isShiftSkipped = false,
  isLastDprSection
}) => {
  // Get skip states from Redux for the current sowId
  const sectionSkips = useSelector(state => {
    if (!sowId) {
      return {
        safety: false,
        quality: false,
        productivity: false,
        personnel: false,
        consumables: false,
        downtime: false
      };
    }
    const skipStates = state.dprReducer?.skipStatesBySection?.[sowId] || {};
    return {
      safety: skipStates.skipSafety || false,
      quality: skipStates.skipQuality || false,
      productivity: skipStates.skipProductivity || false,
      personnel: skipStates.skipPersonnel || false,
      consumables: skipStates.skipConsumables || false,
      downtime: skipStates.skipDowntime || false
    };
  });
  const [remarks, setRemarks] = useState('');
  const [managerName, setManagerName] = useState('');
  const [clientName, setClientName] = useState('');
  const [approverName, setApproverName] = useState('');
  const [signatureData, setSignatureData] = useState({});
  const [signatureTimestamps, setSignatureTimestamps] = useState({});
  const [signatureDrawerOpen, setSignatureDrawerOpen] = useState(false);
  const [signatureType, setSignatureType] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isGeneratingAnyReport, setIsGeneratingAnyReport] = useState(false);
  const [isRefreshingDownload, setIsRefreshingDownload] = useState(false);

  const [isGenerateReportModalVisible, setIsGenerateReportModalVisible] = useState(false);
  const [isSubmitReviewOpen, setIsSubmitReviewOpen] = useState(false);
  const [submitReviewLoading, setSubmitReviewLoading] = useState(false);
  const [submitReviewRows, setSubmitReviewRows] = useState([]);
  const [selectedReportType, setSelectedReportType] = useState('pdf'); // 'flash' | 'pdf' | 'onepage' | 'combined'
  const [reportDeliveryMode, setReportDeliveryMode] = useState('generateOnly'); // 'generateOnly' | 'shareConfigured'
  const [configuredDprEmailCount, setConfiguredDprEmailCount] = useState(null);
  const [modalReportDetails, setModalReportDetails] = useState(null);

  // Combined report: always selectable; API still validates projectId / permissions.
  const canGenerateCombinedReport = true;
  const isOffshore = isOffshoreProject(selectedProject);
  const showThirdSignatureColumn = !isOffshore;
  const crceNameLabel = isOffshore
    ? 'CRCE Project Engineer Name'
    : 'Manager Name';
  const crceSignatureLabel = isOffshore
    ? 'CRCE Project Engineer Signature'
    : 'Manager Signature';
  const clientNameLabel = isOffshore ? 'Client / Contractor Name' : 'Client Name';
  const clientSignatureLabel = isOffshore ? 'Client / Contractor Signature' : 'Client Signature';
  const crceRoleName = isOffshore ? 'Project Engineer' : 'Manager';

  const ongoingPipelineLabel = useMemo(() => {
    if (!ongoingPipelineId || !Array.isArray(projectMainlines) || projectMainlines.length === 0) {
      return '';
    }
    const pipeline = projectMainlines.find((p) => String(p.id) === String(ongoingPipelineId));
    return pipeline ? getMainlineDisplayTitle(pipeline) : '';
  }, [ongoingPipelineId, projectMainlines]);

  useEffect(() => {
    if (!isSubmitReviewOpen || !dprLogId) return undefined;
    let cancelled = false;
    setSubmitReviewLoading(true);
    setSubmitReviewRows([]);
    const pipelinesForReview = isOffshore
      ? (Array.isArray(projectMainlines)
        ? projectMainlines.filter((p) => String(p.id) === String(ongoingPipelineId))
        : [])
      : (Array.isArray(projectMainlines) ? projectMainlines : []);

    fetchDprProductivityLogCached(dprLogId, shift)
      .then((response) => {
        if (cancelled) return;
        setSubmitReviewRows(buildMainlineProductivitySummaries(response, pipelinesForReview));
      })
      .catch(() => {
        if (!cancelled) setSubmitReviewRows([]);
      })
      .finally(() => {
        if (!cancelled) setSubmitReviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isSubmitReviewOpen, dprLogId, shift, isOffshore, ongoingPipelineId, projectMainlines]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    const pt = selectedProject?.projectType ?? selectedProject?.project_type;
    // eslint-disable-next-line no-console
    console.info('[DPR combined report]', {
      projectId: selectedProject?.id,
      projectType: pt,
      combinedOptionEnabled: canGenerateCombinedReport,
    });
  }, [selectedProject?.id, selectedProject?.projectType, selectedProject?.project_type, canGenerateCombinedReport]);

  // Update local state when prop changes
  useEffect(() => {
    // Set isSubmitted flag
    if (dprStatus === 'submitted') {
      setIsSubmitted(true);
    } else {
      setIsSubmitted(false);
    }

    // Prepopulate data if it exists (regardless of status)
    const hasData = initialCommentsData && Object.keys(initialCommentsData).length > 0;

    if (hasData) {
      // Set remarks based on current shift (day vs night comments)
      if (initialCommentsData.dprDayShiftComments !== undefined || initialCommentsData.dprNightShiftComments !== undefined) {
        const remarksToSet = shift === 'day'
          ? (initialCommentsData.dprDayShiftComments || '')
          : (initialCommentsData.dprNightShiftComments || '');
        setRemarks(remarksToSet);
      }

      // Names and signatures are SHARED across both day and night shifts
      if (initialCommentsData.managerName !== undefined) {
        setManagerName(initialCommentsData.managerName || '');
      }
      if (initialCommentsData.clientName !== undefined) {
        setClientName(initialCommentsData.clientName || '');
      }
      if (initialCommentsData.approverName !== undefined) {
        setApproverName(initialCommentsData.approverName || '');
      }
      // Replace signature slots from server props (do not merge) so stale keys never persist after refetch / log change
      const nextSig = {};
      const mSig = String(initialCommentsData.managerSignature ?? '').trim();
      const cSig = String(initialCommentsData.clientSignature ?? '').trim();
      const aSig = String(initialCommentsData.approverSignature ?? '').trim();
      if (mSig) nextSig.manager = mSig;
      if (cSig) nextSig.client = cSig;
      if (aSig) nextSig.approver = aSig;
      setSignatureData(nextSig);
      const nextTs = {};
      if (initialCommentsData.managerSignatureTimestamp) {
        nextTs.manager = initialCommentsData.managerSignatureTimestamp;
      }
      if (initialCommentsData.clientSignatureTimestamp) {
        nextTs.client = initialCommentsData.clientSignatureTimestamp;
      }
      if (initialCommentsData.approverSignatureTimestamp) {
        nextTs.approver = initialCommentsData.approverSignatureTimestamp;
      }
      setSignatureTimestamps(nextTs);
      if (process.env.NODE_ENV === 'development') {
        const short = (s) => (typeof s === 'string' && s.length > 64 ? `${s.slice(0, 64)}…` : s || '(empty)');
        // eslint-disable-next-line no-console
        console.info('[DPR CommentsLog] synced signatures from props', {
          dprLogId,
          dprStatus,
          manager: short(nextSig.manager),
          client: short(nextSig.client),
          approver: short(nextSig.approver),
        });
      }
    } else {
      // Reset to empty if no data
      setRemarks('');
      setManagerName('');
      setClientName('');
      setSignatureData({});
      setSignatureTimestamps({});
      setApproverName('');
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.info('[DPR CommentsLog] cleared form — no commentsData from DPR log', { dprLogId, dprStatus });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dprLogId,
    dprStatus,
    shift,
    initialCommentsData?.dprDayShiftComments,
    initialCommentsData?.dprNightShiftComments,
    initialCommentsData?.managerName,
    initialCommentsData?.clientName,
    initialCommentsData?.approverName,
    initialCommentsData?.managerSignature,
    initialCommentsData?.clientSignature,
    initialCommentsData?.approverSignature,
    initialCommentsData?.managerSignatureTimestamp,
    initialCommentsData?.clientSignatureTimestamp,
    initialCommentsData?.approverSignatureTimestamp
  ]);

  // Update remarks when shift changes for submitted DPRs
  useEffect(() => {
    if (dprStatus === 'submitted' && initialCommentsData) {
      const remarksToSet = shift === 'day'
        ? (initialCommentsData.dprDayShiftComments || '')
        : (initialCommentsData.dprNightShiftComments || '');
      setRemarks(remarksToSet);
    }
  }, [shift, dprStatus, initialCommentsData]);

  const openSignaturePad = (type) => {
    const validTypes = ['manager', 'client', 'approver'];
    if (typeof type !== 'string' || !validTypes.includes(type)) return;
    setSignatureType(type);
    setSignatureDrawerOpen(true);
  };

  const closeSignaturePad = () => {
    setSignatureDrawerOpen(false);
    setSignatureType(null);
  };

  const handleSignatureSave = (base64Image) => {
    if (signatureType) {
      if (base64Image) {
        setSignatureData(prev => ({
          ...prev,
          [signatureType]: base64Image
        }));
        setSignatureTimestamps(prev => ({
          ...prev,
          [signatureType]: moment().toISOString()
        }));
        // Close drawer after state update so saved signature shows in the form
        closeSignaturePad();
      } else {
        setSignatureData(prev => {
          const updated = { ...prev };
          delete updated[signatureType];
          return updated;
        });
        setSignatureTimestamps(prev => {
          const updated = { ...prev };
          delete updated[signatureType];
          return updated;
        });
        closeSignaturePad();
      }
    }
  };

  // Build DPR payload according to API contract (skip options, shift, section skips, comments for both shifts)
  const buildDprPayload = ({
    dprLogId,
    shift,
    dprDayShiftComments,
    dprNightShiftComments,
    skipDayShift,
    skipNightShift,
    skipDpr,
    sectionSkips,
    managerName,
    clientName,
    managerSignature,
    clientSignature,
    signatureTimestamps,
    status,
    ongoingPipelineId: pipelineIdForPayload,
    dprProjectStatus: projectStatusForPayload,
  }) => {
    const isDay = shift === 'day';

    return {
      dprLog: {
        dprLogId: dprLogId,
        status: status || 'draft',
        dprDayShiftComments: dprDayShiftComments ?? '',
        dprNightShiftComments: dprNightShiftComments ?? '',

        ...(pipelineIdForPayload != null ? { ongoingPipelineId: pipelineIdForPayload } : {}),
        ...(projectStatusForPayload != null && String(projectStatusForPayload).trim() !== ''
          ? { dprProjectStatus: String(projectStatusForPayload).trim() }
          : {}),

        isSkippedDayShift: skipDayShift ? 1 : 0,
        isSkippedNightShift: skipNightShift ? 1 : 0,
        isSkippedDpr: skipDpr ? 1 : 0,

        isSafetySkippedDayShift: isDay ? (sectionSkips.safety ? 1 : 0) : 0,
        isSafetySkippedNightShift: !isDay ? (sectionSkips.safety ? 1 : 0) : 0,

        isQualitySkippedDayShift: isDay ? (sectionSkips.quality ? 1 : 0) : 0,
        isQualitySkippedNightShift: !isDay ? (sectionSkips.quality ? 1 : 0) : 0,

        isProductivitySkippedDayShift: isDay ? (sectionSkips.productivity ? 1 : 0) : 0,
        isProductivitySkippedNightShift: !isDay ? (sectionSkips.productivity ? 1 : 0) : 0,

        isPersonnelSkippedDayShift: isDay ? (sectionSkips.personnel ? 1 : 0) : 0,
        isPersonnelSkippedNightShift: !isDay ? (sectionSkips.personnel ? 1 : 0) : 0,

        isConsumablesSkippedDayShift: isDay ? (sectionSkips.consumables ? 1 : 0) : 0,
        isConsumablesSkippedNightShift: !isDay ? (sectionSkips.consumables ? 1 : 0) : 0,

        isDowntimeSkippedDayShift: isDay ? (sectionSkips.downtime ? 1 : 0) : 0,
        isDowntimeSkippedNightShift: !isDay ? (sectionSkips.downtime ? 1 : 0) : 0,

        approvedBy1: managerSignature && managerName ? {
          teamMemberName: managerName,
          roleName: crceRoleName,
          signature: managerSignature.includes(',') ? managerSignature.split(',')[1] : managerSignature,
          signedAt: signatureTimestamps.manager || moment().toISOString()
        } : null,
        approvedBy2: clientSignature && clientName ? {
          teamMemberName: clientName,
          roleName: "Client",
          signature: clientSignature.includes(',') ? clientSignature.split(',')[1] : clientSignature,
          signedAt: signatureTimestamps.client || moment().toISOString()
        } : null,
        approvedBy3: signatureData.approver && approverName ? {
          teamMemberName: approverName,
          roleName: "Approver",
          signature: signatureData.approver.includes(',') ? signatureData.approver.split(',')[1] : signatureData.approver,
          signedAt: signatureTimestamps.approver || moment().toISOString()
        } : null
      }
    };
  };

  // Check if mandatory fields are filled
  const isFormValid = () => {
    if (requireOngoingPipeline && !ongoingPipelineId) return false;
    return managerName.trim() !== '' && signatureData.manager;
  };

  const resolveOngoingPipelineIdForPayload = () => {
    if (requireOngoingPipeline) return ongoingPipelineId ?? null;
    return ongoingPipelineId ?? sowId ?? null;
  };

  const handleSave = async () => {
    if (isShiftSkipped) return false;
    if (!dprLogId) {
      message.error('DPR Log ID is required to save');
      return false;
    }

    setIsSaving(true);
    try {
      const dprDayShiftComments = shift === 'day' ? remarks : (initialCommentsData.dprDayShiftComments ?? '');
      const dprNightShiftComments = shift === 'night' ? remarks : (initialCommentsData.dprNightShiftComments ?? '');
      const payload = buildDprPayload({
        dprLogId,
        shift,
        dprDayShiftComments,
        dprNightShiftComments,
        skipDayShift,
        skipNightShift,
        skipDpr,
        sectionSkips: sectionSkips,
        managerName,
        clientName,
        managerSignature: signatureData.manager,
        clientSignature: signatureData.client,
        signatureTimestamps,
        status: 'draft',
        ongoingPipelineId: resolveOngoingPipelineIdForPayload(),
        dprProjectStatus,
      });

      console.log('Saving DPR log with payload:', payload);

      const response = await axiosConfig.put(`/dpr-log/${dprLogId}`, payload);
      const responseData = response?.data?.data || response?.data;

      message.success({
        content: 'DPR saved successfully!',
        duration: 3
      });

      setIsSubmitted(false);

      // Prepopulate comment box from response if available
      if (responseData) {
        const updatedComments = shift === 'day'
          ? (responseData.dprDayShiftComments ?? remarks)
          : (responseData.dprNightShiftComments ?? remarks);
        if (updatedComments !== undefined) {
          setRemarks(updatedComments);
        }
      }

      if (onSave) {
        onSave({
          remarks,
          managerName,
          clientName,
          managerSignature: signatureData.manager,
          clientSignature: signatureData.client,
          skipSections: sectionSkips,
          responseData
        });
      }
      return true;
    } catch (error) {
      console.error('Error saving DPR:', error);
      message.error({
        content: 'Failed to save DPR. Please try again.',
        duration: 3
      });
      setIsSubmitted(false);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitClick = () => {
    if (!dprLogId) {
      message.error('DPR Log ID is required to submit');
      return;
    }

    if (requireOngoingPipeline && !ongoingPipelineId) {
      message.warning('Please select an ongoing pipeline before submitting.');
      return;
    }

    if (!managerName.trim() || !signatureData.manager) {
      message.warning('Please add the mandatory signature before submitting.');
      return;
    }

    setIsSubmitReviewOpen(true);
  };

  const executeSubmit = async () => {
    if (!dprLogId) {
      message.error('DPR Log ID is required to submit');
      return;
    }

    setIsSubmittingComment(true);
    try {
      const dprDayShiftComments = shift === 'day' ? remarks : (initialCommentsData.dprDayShiftComments ?? '');
      const dprNightShiftComments = shift === 'night' ? remarks : (initialCommentsData.dprNightShiftComments ?? '');
      const payload = buildDprPayload({
        dprLogId,
        shift,
        dprDayShiftComments,
        dprNightShiftComments,
        skipDayShift,
        skipNightShift,
        skipDpr,
        sectionSkips: sectionSkips,
        managerName,
        clientName,
        managerSignature: signatureData.manager,
        clientSignature: signatureData.client,
        signatureTimestamps,
        status: 'submitted',
        ongoingPipelineId: resolveOngoingPipelineIdForPayload(),
        dprProjectStatus,
      });

      console.log('Submitting DPR log with payload:', payload);

      const response = await axiosConfig.put(`/dpr-log/${dprLogId}`, payload);
      const responseData = response?.data?.data || response?.data;

      message.success({
        content: 'DPR submitted successfully!',
        duration: 3
      });

      setIsSubmitted(true);
      setIsSubmitReviewOpen(false);

      // Prepopulate comment box from response if available
      if (responseData) {
        const updatedComments = shift === 'day'
          ? (responseData.dprDayShiftComments ?? remarks)
          : (responseData.dprNightShiftComments ?? remarks);
        if (updatedComments !== undefined) {
          setRemarks(updatedComments);
        }
      }

      if (onSave) {
        onSave({
          remarks,
          managerName,
          clientName,
          managerSignature: signatureData.manager,
          clientSignature: signatureData.client,
          skipSections: sectionSkips,
          responseData
        });
      }
    } catch (error) {
      console.error('Error submitting DPR:', error);
      message.error({
        content: 'Failed to submit DPR. Please try again.',
        duration: 3
      });
      setIsSubmitted(false);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const fetchConfiguredDprReportEmails = async () => {
    if (!selectedProject?.id) return [];
    const res = await axiosConfig.get(`/project?projectIds=${selectedProject.id}`);
    return getConfiguredDprReportEmails(res?.data?.data);
  };

  const openGenerateReportModal = () => {
    const reportSubmitted = isSubmitted || dprStatus === 'submitted';
    if (process.env.NODE_ENV === 'development') {
      const pt = selectedProject?.projectType ?? selectedProject?.project_type;
      // eslint-disable-next-line no-console
      console.info('[DPR Generate Report modal opened]', {
        projectId: selectedProject?.id,
        projectType: pt,
        combinedOptionEnabled: true,
        dprLogId,
        reportSubmitted,
      });
    }
    setSelectedReportType(reportSubmitted ? 'pdf' : 'flash');
    setReportDeliveryMode('generateOnly');
    setConfiguredDprEmailCount(null);
    setModalReportDetails(dprReportDetails);
    setIsGenerateReportModalVisible(true);
    fetchConfiguredDprReportEmails()
      .then((emails) => setConfiguredDprEmailCount(emails.length))
      .catch(() => setConfiguredDprEmailCount(0));
  };

  const closeGenerateReportModal = () => {
    setIsGenerateReportModalVisible(false);
    setSelectedReportType('pdf');
    setReportDeliveryMode('generateOnly');
    setConfiguredDprEmailCount(null);
    setModalReportDetails(null);
  };

  useEffect(() => {
    if (!isGenerateReportModalVisible) return;
    setModalReportDetails(dprReportDetails);
  }, [dprReportDetails, isGenerateReportModalVisible]);

  useEffect(() => {
    if (!isGenerateReportModalVisible || !dprLogId) return;
    if (selectedReportType === 'flash') return;

    const apiReportType = mapUiReportTypeToApi(selectedReportType);
    if (!apiReportType) return;

    const entry = getStoredDprReportEntry(modalReportDetails ?? dprReportDetails, selectedReportType);
    if (!hasGeneratedStoredReport(entry) || extractReportGeneratedAt(entry)) {
      return;
    }

    let cancelled = false;
    refreshReportDownloadUrl(dprLogId, apiReportType)
      .then((data) => {
        if (cancelled || !data) return;
        const refreshedEntry = buildReportEntryFromGenerateResponse(data);
        setModalReportDetails((prev) => upsertDprReportDetailsEntry(
          prev ?? dprReportDetails,
          apiReportType,
          { ...entry, ...refreshedEntry }
        ));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [
    isGenerateReportModalVisible,
    selectedReportType,
    dprLogId,
    dprReportDetails,
    modalReportDetails,
  ]);

  const handleRedownloadStoredReport = async () => {
    const apiReportType = mapUiReportTypeToApi(selectedReportType);
    if (!apiReportType || !dprLogId) return;

    const formattedDate = moment(logDate || new Date()).format('YYYY-MM-DD');
    const filename = getDprReportFilename(selectedReportType, formattedDate, shift);
    const cachedUrl = getStoredDprReportEntry(modalReportDetails ?? dprReportDetails, selectedReportType)?.fileUrl;

    if (cachedUrl) {
      triggerReportDownload(cachedUrl, filename);
      message.success('Report download started.');
      return;
    }

    setIsRefreshingDownload(true);
    try {
      const data = await refreshReportDownloadUrl(dprLogId, apiReportType);
      if (isReportReadyForDownload(data)) {
        triggerReportDownload(data.fileUrl, filename);
        setModalReportDetails((prev) => upsertDprReportDetailsEntry(
          prev ?? dprReportDetails,
          apiReportType,
          buildReportEntryFromGenerateResponse(data)
        ));
        message.success('Report download started.');
      } else if (data.reportStatus === DPR_REPORT_STATUS.ERROR) {
        message.error('Last report generation failed. Please generate again.');
      } else {
        message.info('No report is available for download yet.');
      }
    } catch (err) {
      console.error('Error refreshing report download URL:', err);
      message.error(err.message || 'Failed to get download link.');
    } finally {
      setIsRefreshingDownload(false);
    }
  };

  const handleGenerateSelectedReport = async () => {
    const isFlashReport = selectedReportType === 'flash';
    const reportSubmitted = isSubmitted || dprStatus === 'submitted';

    if (!dprLogId) {
      message.error('DPR Log ID is required to generate report');
      return;
    }

    if (!reportSubmitted && !isFlashReport) {
      message.warning('Submit the DPR before generating this report type. Flash report is available before submit.');
      return;
    }

    if (isFlashReport) {
      const shareEmails = await fetchConfiguredDprReportEmails();
      if (!shareEmails.length) {
        message.error(
          'No DPR reporting emails configured for this project. Add recipients in DPR Settings → Reporting Email.'
        );
        return;
      }

      setIsGeneratingAnyReport(true);
      try {
        await generateFlashReport({
          dprLogId,
          shift,
          emailToDestinations: shareEmails,
        });
        message.success('Flash report generated and emailed to configured recipients.');
        closeGenerateReportModal();
      } catch (err) {
        console.error('Error generating flash report:', err);
        message.error(err.message || 'Failed to generate flash report.');
      } finally {
        setIsGeneratingAnyReport(false);
      }
      return;
    }

    const reportType = mapUiReportTypeToApi(selectedReportType);
    const output = mapUiOutputToApi(reportDeliveryMode);
    const formattedDate = moment(logDate || new Date()).format('YYYY-MM-DD');

    setIsGeneratingAnyReport(true);
    try {
      const data = await generateDprReport({ dprLogId, reportType, output });

      const reportEntry = buildReportEntryFromGenerateResponse(data);
      setModalReportDetails((prev) => upsertDprReportDetailsEntry(
        prev ?? dprReportDetails,
        reportType,
        reportEntry
      ));

      if (output === 'download') {
        if (data.fileUrl) {
          triggerReportDownload(
            data.fileUrl,
            getDprReportFilename(selectedReportType, formattedDate, shift)
          );
        }
        const typeLabel = selectedReportType === 'onepage'
          ? 'One page report'
          : selectedReportType === 'combined'
            ? 'Combined report'
            : 'Welding report';
        message.success(data.fileUrl
          ? `${typeLabel} downloaded to your device.`
          : `${typeLabel} generated successfully.`);
      } else if (data.emailSent && data.emailCount > 0) {
        message.success(`Report emailed to ${data.emailCount} recipient${data.emailCount === 1 ? '' : 's'}.`);
      } else {
        message.warning(
          'Report generated, but no recipients are configured in DPR Settings → Reporting Email.'
        );
      }

      if (onReportGenerated) onReportGenerated();
    } catch (err) {
      console.error('Error generating report:', err);
      message.error(err.message || 'Failed to generate report. Please try again.');
    } finally {
      setIsGeneratingAnyReport(false);
    }
  };

  const isGenerateReportBusy = isGeneratingAnyReport || isRefreshingDownload;
  const isReportSubmitted = isSubmitted || dprStatus === 'submitted';
  const isFlashReport = selectedReportType === 'flash';
  const showDeliveryOptions = !isFlashReport;
  const isShareModeBlocked = isFlashReport && configuredDprEmailCount === 0;
  const effectiveReportDetails = modalReportDetails ?? dprReportDetails;
  const storedReportEntry = getStoredDprReportEntry(effectiveReportDetails, selectedReportType);
  const hasStoredReport = !isFlashReport && hasGeneratedStoredReport(storedReportEntry);
  const isDownloadAction = !isFlashReport && reportDeliveryMode === 'generateOnly';
  const primaryReportActionLabel = isGenerateReportBusy
    ? 'Generating report…'
    : isDownloadAction
      ? DPR_DOWNLOAD_PDF_LABEL
      : 'Generate & Email';
  const PrimaryReportActionIcon = isDownloadAction ? Download : Mail;
  return (
    <div style={{
      padding: 0,
      minHeight: '100vh',
      backgroundColor: 'var(--modern-bg-content, #F8FAFC)',
      textAlign: 'left'
    }}>
      {/* Main Content Card */}
      <div style={{
        backgroundColor: 'var(--modern-bg-primary, #FFFFFF)',
        borderRadius: 'var(--modern-border-radius, 12px)',
        padding: 'var(--modern-spacing-xl, 32px)',
        boxShadow: 'var(--modern-shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.1))',
        maxWidth: '100%',
        textAlign: 'left'
      }}>
        {/* Header with Icon */}
        <div style={{ marginBottom: 'var(--modern-spacing-lg, 24px)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '8px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: 'var(--modern-border-radius, 8px)',
              backgroundColor: 'var(--modern-bg-secondary, #F3F4F6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <MessageOutlined style={{ color: 'var(--modern-text-secondary, #4B5563)', fontSize: '20px' }} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{
                margin: '0 0 4px 0',
                color: 'var(--modern-text-primary, #111827)',
                fontSize: 'var(--modern-font-size-xl, 20px)',
                fontWeight: 600,
                lineHeight: '28px',
                textAlign: 'left'
              }}>
                Overall Comments
              </h2>
              <p style={{
                margin: 0,
                color: 'var(--modern-text-secondary, #6B7280)',
                fontSize: 'var(--modern-font-size-base, 14px)',
                lineHeight: '20px',
                textAlign: 'left'
              }}>
                Add any general remarks or overall feedback for the report.
              </p>
            </div>
          </div>
        </div>

        {/* CommentBox */}
        <CommentBox
          header="Notes / Remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Enter overall comments here..."
        />

        {/* Manager Name, Client Name, Approver Name Section */}
        <div style={commentsFieldGridStyle}>
          <div style={commentsFieldCellStyle}>
            <label style={{
              display: 'block',
              color: 'var(--modern-text-primary, #374151)',
              fontSize: 'var(--modern-font-size-base, 14px)',
              fontWeight: 500,
              marginBottom: '8px',
              textAlign: 'left'
            }}>
              {crceNameLabel} <span style={{ color: '#ff4d4f' }}>*</span>
            </label>
            <input
              type="text"
              placeholder={crceNameLabel}
              value={managerName}
              onChange={(e) => setManagerName(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 12px',
                borderRadius: 'var(--modern-border-radius, 8px)',
                fontSize: 'var(--modern-font-size-base, 14px)',
                height: '40px',
                backgroundColor: '#FFFFFF',
                border: '1px solid var(--modern-border-color, #E5E7EB)',
                color: 'var(--modern-text-primary, #1F2937)',
                outline: 'none',
                fontFamily: 'inherit'
              }}
            />
          </div>
          <div style={commentsFieldCellStyle}>
            <label style={{
              display: 'block',
              color: 'var(--modern-text-primary, #374151)',
              fontSize: 'var(--modern-font-size-base, 14px)',
              fontWeight: 500,
              marginBottom: '8px',
              textAlign: 'left'
            }}>
              {clientNameLabel}
            </label>
            <input
              type="text"
              placeholder={clientNameLabel}
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 12px',
                borderRadius: 'var(--modern-border-radius, 8px)',
                fontSize: 'var(--modern-font-size-base, 14px)',
                height: '40px',
                backgroundColor: '#FFFFFF',
                border: '1px solid var(--modern-border-color, #E5E7EB)',
                color: 'var(--modern-text-primary, #1F2937)',
                outline: 'none',
                fontFamily: 'inherit'
              }}
            />
          </div>
          {showThirdSignatureColumn && (
          <div style={commentsFieldCellStyle}>
            <label style={{
              display: 'block',
              color: 'var(--modern-text-primary, #374151)',
              fontSize: 'var(--modern-font-size-base, 14px)',
              fontWeight: 500,
              marginBottom: '8px',
              textAlign: 'left'
            }}>
              Approver Name
            </label>
            <input
              type="text"
              placeholder="Approver Name"
              value={approverName}
              onChange={(e) => setApproverName(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 12px',
                borderRadius: 'var(--modern-border-radius, 8px)',
                fontSize: 'var(--modern-font-size-base, 14px)',
                height: '40px',
                backgroundColor: '#FFFFFF',
                border: '1px solid var(--modern-border-color, #E5E7EB)',
                color: 'var(--modern-text-primary, #1F2937)',
                outline: 'none',
                fontFamily: 'inherit'
              }}
            />
          </div>
          )}
        </div>

        {/* Manager Signature, Client Signature, Approver Signature Section */}
        <div style={commentsFieldGridStyle}>
          <div style={commentsFieldCellStyle}>
            <div style={{
              color: 'var(--modern-text-primary, #374151)',
              fontSize: 'var(--modern-font-size-base, 14px)',
              fontWeight: 500,
              marginBottom: '8px',
              textAlign: 'left'
            }}>
              {crceSignatureLabel} <span style={{ color: '#ff4d4f' }}>*</span>
            </div>
            {!signatureData.manager ? (
              <button
                type="button"
                onClick={() => openSignaturePad('manager')}
                style={{
                  ...commentsSignatureBoxStyle,
                  height: '60px',
                  border: '1px dashed var(--modern-border-color, #E5E7EB)',
                  borderRadius: 'var(--modern-border-radius, 8px)',
                  backgroundColor: '#FFFFFF',
                  color: 'var(--modern-text-secondary, #6B7280)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontSize: 'var(--modern-font-size-base, 14px)',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit'
                }}
              >
                <PenLine size={18} strokeWidth={2} />
                Add Signature
              </button>
            ) : (
              <div
                style={{
                  ...commentsSignatureBoxStyle,
                  cursor: 'pointer',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '8px',
                  backgroundColor: '#FFFFFF',
                  height: '160px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
                onClick={() => openSignaturePad('manager')}
              >
                <img
                  src={signatureData.manager}
                  alt="Manager Signature"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.target.onerror = null;
                    if (process.env.NODE_ENV === 'development') {
                      // eslint-disable-next-line no-console
                      console.warn('[DPR Comments] Manager signature image failed to load', {
                        srcPrefix: String(e.target?.src || '').slice(0, 120),
                      });
                    }
                    e.target.style.display = 'none';
                  }}
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '120px',
                    objectFit: 'contain'
                  }}
                />
                {signatureTimestamps.manager && (
                  <div style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#6B7280',
                    textAlign: 'center'
                  }}>
                    Signed: {moment(signatureTimestamps.manager).format('DD/MM/YYYY hh:mm A')}
                  </div>
                )}
              </div>
            )}
          </div>
          <div style={commentsFieldCellStyle}>
            <div style={{
              color: 'var(--modern-text-primary, #374151)',
              fontSize: 'var(--modern-font-size-base, 14px)',
              fontWeight: 500,
              marginBottom: '8px',
              textAlign: 'left'
            }}>
              {clientSignatureLabel}
            </div>
            {!signatureData.client ? (
              <button
                type="button"
                onClick={() => openSignaturePad('client')}
                style={{
                  ...commentsSignatureBoxStyle,
                  height: '60px',
                  border: '1px dashed var(--modern-border-color, #E5E7EB)',
                  borderRadius: 'var(--modern-border-radius, 8px)',
                  backgroundColor: '#FFFFFF',
                  color: 'var(--modern-text-secondary, #6B7280)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontSize: 'var(--modern-font-size-base, 14px)',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit'
                }}
              >
                <PenLine size={18} strokeWidth={2} />
                Add Signature
              </button>
            ) : (
              <div
                style={{
                  ...commentsSignatureBoxStyle,
                  cursor: 'pointer',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '8px',
                  backgroundColor: '#FFFFFF',
                  height: '160px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
                onClick={() => openSignaturePad('client')}
              >
                <img
                  src={signatureData.client}
                  alt="Client Signature"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.target.onerror = null;
                    if (process.env.NODE_ENV === 'development') {
                      // eslint-disable-next-line no-console
                      console.warn('[DPR Comments] Client signature image failed to load', {
                        srcPrefix: String(e.target?.src || '').slice(0, 120),
                      });
                    }
                    e.target.style.display = 'none';
                  }}
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '120px',
                    objectFit: 'contain'
                  }}
                />
                {signatureTimestamps.client && (
                  <div style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#6B7280',
                    textAlign: 'center'
                  }}>
                    Signed: {moment(signatureTimestamps.client).format('DD/MM/YYYY hh:mm A')}
                  </div>
                )}
              </div>
            )}
          </div>
          {showThirdSignatureColumn && (
          <div style={commentsFieldCellStyle}>
            <div style={{
              color: 'var(--modern-text-primary, #374151)',
              fontSize: 'var(--modern-font-size-base, 14px)',
              fontWeight: 500,
              marginBottom: '8px',
              textAlign: 'left'
            }}>
              Approver Signature
            </div>
            {!signatureData.approver ? (
              <button
                type="button"
                onClick={() => openSignaturePad('approver')}
                style={{
                  ...commentsSignatureBoxStyle,
                  height: '60px',
                  border: '1px dashed var(--modern-border-color, #E5E7EB)',
                  borderRadius: 'var(--modern-border-radius, 8px)',
                  backgroundColor: '#FFFFFF',
                  color: 'var(--modern-text-secondary, #6B7280)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontSize: 'var(--modern-font-size-base, 14px)',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit'
                }}
              >
                <PenLine size={18} strokeWidth={2} />
                Add Signature
              </button>
            ) : (
              <div
                style={{
                  ...commentsSignatureBoxStyle,
                  cursor: 'pointer',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '8px',
                  backgroundColor: '#FFFFFF',
                  height: '160px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
                onClick={() => openSignaturePad('approver')}
              >
                <img
                  src={signatureData.approver}
                  alt="Approver Signature"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.target.onerror = null;
                    if (process.env.NODE_ENV === 'development') {
                      // eslint-disable-next-line no-console
                      console.warn('[DPR Comments] Approver signature image failed to load', {
                        srcPrefix: String(e.target?.src || '').slice(0, 120),
                      });
                    }
                    e.target.style.display = 'none';
                  }}
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '120px',
                    objectFit: 'contain'
                  }}
                />
                {signatureTimestamps.approver && (
                  <div style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#6B7280',
                    textAlign: 'center'
                  }}>
                    Signed: {moment(signatureTimestamps.approver).format('DD/MM/YYYY hh:mm A')}
                  </div>
                )}
              </div>
            )}
          </div>
          )}
        </div>

        {/* Action Buttons - Save/Submit use native button so disabled works (ui-components Button does not support disabled) */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '12px', marginTop: '32px' }}>
          <Button
            label="Cancel"
            onClick={handleCancel}
            variant="tertiary"
            size="medium"
            style={{
              backgroundColor: 'transparent',
              borderColor: 'var(--modern-border-color, #D1D5DB)',
              color: 'var(--modern-text-primary, #374151)'
            }}
          />
          <Button
            label={
              isSaving
                ? 'Saving...'
                : (typeof isLastDprSection === 'boolean'
                  ? (isLastDprSection ? 'Save section' : 'Save & Next')
                  : 'Save')
            }
            onClick={handleSave}
            disabled={!dprLogId || isSaving}
            variant="secondary"
            icon={isSaving ? LoadingOutlined : Save}
            size="medium"
            style={{
              backgroundColor: (!dprLogId || isSaving) ? '#E5E7EB' : '#0F172A',
              borderColor: (!dprLogId || isSaving) ? '#E5E7EB' : '#0F172A',
              color: '#FFFFFF',
              opacity: (!dprLogId || isSaving) ? 0.7 : 1,
              cursor: (!dprLogId || isSaving) ? 'not-allowed' : 'pointer'
            }}
          />
          <Button
            label={isSubmittingComment ? 'Submitting...' : 'Submit'}
            onClick={handleSubmitClick}
            disabled={dprStatus === 'submitted' || !isFormValid() || isSubmittingComment}
            variant="secondary"
            icon={isSubmittingComment ? LoadingOutlined : null}
            size="medium"
            style={{
              backgroundColor: (dprStatus === 'submitted' || !isFormValid() || isSubmittingComment) ? '#FEE2E2' : '#991B1B',
              borderColor: (dprStatus === 'submitted' || !isFormValid() || isSubmittingComment) ? '#FEE2E2' : '#991B1B',
              color: '#FFFFFF',
              opacity: (dprStatus === 'submitted' || !isFormValid() || isSubmittingComment) ? 0.8 : 1,
              cursor: (dprStatus === 'submitted' || !isFormValid() || isSubmittingComment) ? 'not-allowed' : 'pointer'
            }}
          />
          <Button
            label={isGenerateReportBusy ? 'Generating report…' : 'Generate Report'}
            onClick={openGenerateReportModal}
            disabled={!dprLogId || isGenerateReportBusy}
            variant="secondary"
            icon={FileText}
            size="medium"
            style={{
              backgroundColor: (!dprLogId || isGenerateReportBusy) ? '#FEE2E2' : '#991B1B',
              borderColor: (!dprLogId || isGenerateReportBusy) ? '#FEE2E2' : '#991B1B',
              color: '#FFFFFF',
              opacity: (!dprLogId || isGenerateReportBusy) ? 0.8 : 1,
              cursor: (!dprLogId || isGenerateReportBusy) ? 'not-allowed' : 'pointer'
            }}
          />
        </div>
      </div>

      <Drawer
        title={
          <span style={{
            color: 'var(--modern-text-primary, #111827)',
            fontSize: 'var(--modern-font-size-lg, 16px)',
            fontWeight: 600
          }}>
            {signatureType === 'manager' ? 'Manager' : signatureType === 'client' ? 'Client' : 'Approver'} signature
          </span>
        }
        placement="right"
        onClose={closeSignaturePad}
        visible={signatureDrawerOpen}
        width={520}
        bodyStyle={{
          padding: '20px',
          backgroundColor: 'var(--modern-bg-content, #F8FAFC)',
          maxHeight: 'calc(100vh - 55px)',
          overflow: 'auto'
        }}
        headerStyle={{
          borderBottom: '1px solid var(--modern-border-color, #E5E7EB)',
          backgroundColor: 'var(--modern-bg-primary, #FFFFFF)',
          padding: '16px 24px'
        }}
      >
        <DSRSignaturePad
          visible={signatureDrawerOpen}
          onClose={closeSignaturePad}
          onSave={handleSignatureSave}
          title={signatureType === 'manager' ? 'Manager' : signatureType === 'client' ? 'Client' : 'Approver'}
          initialSignature={signatureType ? signatureData[signatureType] : null}
        />
      </Drawer>

      <Modal
        title={<h3 className="modern-dsr-dt-add-modal__h-title">Generate Report</h3>}
        visible={isGenerateReportModalVisible}
        onCancel={closeGenerateReportModal}
        centered
        width={640}
        className="modern-dsr-dt-add-modal modern-generate-report-modal"
        wrapClassName="modern-dsr-dt-add-modal__wrap"
        footer={(
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            <Button label="Cancel" variant="tertiary" onClick={closeGenerateReportModal} size="medium" />
            <Button
              label={primaryReportActionLabel}
              variant="primary"
              onClick={handleGenerateSelectedReport}
              disabled={isGenerateReportBusy || isShareModeBlocked}
              size="medium"
              icon={PrimaryReportActionIcon}
            />
          </div>
        )}
      >
        <div className="modern-dsr-dt-add-modal__form-scope">
          <div className="generate-report-modal-sections">
            <div className="generate-report-section generate-report-section--boxed">
              <div className="generate-report-section-label">Report type</div>
              <Radio.Group
                className="report-radio"
                value={selectedReportType}
                onChange={(e) => {
                  const nextType = e.target.value;
                  if (!isReportSubmitted && nextType !== 'flash') return;
                  setSelectedReportType(nextType);
                  if (nextType === 'flash') {
                    setReportDeliveryMode('shareConfigured');
                  }
                }}
                style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
              >
                <Radio value="flash">Flash Report</Radio>
                <Radio value="pdf" disabled={!isReportSubmitted}>Welding Report</Radio>
                <Radio value="onepage" disabled={!isReportSubmitted}>One Page Report</Radio>
                <Radio value="combined" disabled={!isReportSubmitted || !canGenerateCombinedReport}>
                  Combined Report
                </Radio>
              </Radio.Group>
              {!isReportSubmitted ? (
                <p className="generate-report-hint generate-report-hint--info">
                  Flash report is available before submit. Other report types unlock after the DPR is submitted.
                </p>
              ) : null}
            </div>

            {isFlashReport && (
              <div className="generate-report-section generate-report-section--boxed generate-report-section--delivery">
                <div className="generate-report-section-label">Output</div>
                <p className="generate-report-hint">
                  {configuredDprEmailCount === null
                    ? 'Checking configured recipients…'
                    : configuredDprEmailCount > 0
                      ? `Flash reports are email only. ${configuredDprEmailCount} recipient${configuredDprEmailCount === 1 ? '' : 's'} will receive this report. Manage the list in DPR Settings → Reporting Email.`
                      : 'Flash reports are email only. Add recipients in DPR Settings → Reporting Email before sending.'}
                </p>
              </div>
            )}

            {showDeliveryOptions && (
              <div className="generate-report-section generate-report-section--boxed generate-report-section--delivery">
                <div className="generate-report-section-label">Output</div>
                <Radio.Group
                  className="report-radio"
                  value={reportDeliveryMode}
                  onChange={(e) => setReportDeliveryMode(e.target.value)}
                  style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
                >
                  <Radio value="generateOnly">
                    <span className="generate-report-radio-label">
                      <Download size={15} aria-hidden />
                      {DPR_DOWNLOAD_PDF_LABEL}
                    </span>
                  </Radio>
                  <Radio value="shareConfigured">
                    <span className="generate-report-radio-label">
                      <Mail size={15} aria-hidden />
                      Email to configured recipients
                    </span>
                  </Radio>
                </Radio.Group>
                {reportDeliveryMode === 'generateOnly' && (
                  <p className="generate-report-hint generate-report-hint--info">
                    The PDF will download automatically after generation.
                  </p>
                )}
                {reportDeliveryMode === 'shareConfigured' && (
                  <p className={`generate-report-hint${configuredDprEmailCount === 0 ? '' : ' generate-report-hint--info'}`}>
                    {configuredDprEmailCount === null
                      ? 'Checking configured recipients…'
                      : configuredDprEmailCount > 0
                        ? `${configuredDprEmailCount} recipient${configuredDprEmailCount === 1 ? '' : 's'} will receive this report. Manage the list in DPR Settings → Reporting Email.`
                        : 'No recipients configured yet. Add email addresses in DPR Settings → Reporting Email to use this option.'}
                  </p>
                )}
              </div>
            )}

            {!isFlashReport && (hasStoredReport || storedReportEntry?.reportStatus === DPR_REPORT_STATUS.ERROR) && (
              <div className="generate-report-section generate-report-section--boxed generate-report-section--saved">
                <div className="generate-report-section-label">Saved report</div>
                {storedReportEntry?.reportStatus === DPR_REPORT_STATUS.ERROR && (
                  <p className="generate-report-hint generate-report-hint--error">
                    Last generation failed. Please try again.
                  </p>
                )}
                {hasStoredReport && (
                  <div className="generate-report-stored-box">
                    <button
                      type="button"
                      className="generate-report-stored-download-btn"
                      onClick={handleRedownloadStoredReport}
                      disabled={isGenerateReportBusy}
                    >
                      <Download size={15} aria-hidden />
                      {isRefreshingDownload ? 'Downloading…' : DPR_DOWNLOAD_PDF_LABEL}
                    </button>
                    <p className="generate-report-stored-info">
                      {formatDprReportGeneratedLabel(storedReportEntry)}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>

      <DprSubmitReviewModal
        isOpen={isSubmitReviewOpen}
        isLoading={submitReviewLoading}
        isSubmitting={isSubmittingComment}
        isOffshore={isOffshore}
        ongoingPipelineLabel={ongoingPipelineLabel}
        productivityRows={submitReviewRows}
        remarks={remarks}
        managerName={managerName}
        clientName={clientName}
        approverName={approverName}
        crceNameLabel={crceNameLabel}
        clientNameLabel={clientNameLabel}
        showApproverField={showThirdSignatureColumn}
        onConfirm={executeSubmit}
        onCancel={() => {
          if (!isSubmittingComment) setIsSubmitReviewOpen(false);
        }}
      />
    </div>
  );
};

export default CommentsLog;
